import React from 'react';
import styled from 'styled-components';

import { device } from '@src/common/breakpoints';
import CloseIcon from '@src/icons/close-icon.svg';
import { getContrastColorFrom } from '@src/common/colors';
import { useWidgetParam, useWidgetsTheme as theme } from '@src/core/hooks';
import { BodyRegularMedium, Heading5, Heading6 } from '@src/fonts/typography/Typography';
import ProductNavigationIcon, { StepTypes } from '@src/components/Product/StepNavigationIcon';
import usePackageIdParam from '@src/hooks/usePackageIdParam';

type Props = {
  title: string;
  steps: string[];
  currentStep?: string;
  onClose?: () => void;
  stepType: StepTypes;
};

type HeaderProps = {
  mainColor: string;
  textColor: string;
};

const HeaderStepsNavigation = (props: Props) => {
  const { title, steps, currentStep, onClose, stepType } = props;

  const mainColor = theme('actionColor');
  const textColor = getContrastColorFrom(mainColor);
  const enableModal = useWidgetParam('enable-modal');
  const businessName = useWidgetParam('business-name');

  const packageId = usePackageIdParam();
  const showAsModal = enableModal && enableModal == 'true';

  const closeModal = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Header mainColor={mainColor} textColor={textColor}>
      <HeaderArea>
        <ProductInformation>
          <Title>{title}</Title>

          <ProductOriginLabel>{businessName}</ProductOriginLabel>
        </ProductInformation>

        {showAsModal && (
          <HeaderCloseButton
            data-cy='productAdderModalCloseButton'
            mainColor={mainColor}
            textColor={textColor}
            onClick={closeModal}
          >
            <CloseIcon />
          </HeaderCloseButton>
        )}
      </HeaderArea>

      {!packageId ? (
        <HeaderContent>
          <NavigationSteps>
            {steps.map((step, index) =>
              step !== 'finish' ? (
                <ProductNavigationIcon
                  key={step}
                  type={step}
                  number={index + 1}
                  stepType={stepType}
                  mainColor={mainColor}
                  colorTitle={textColor}
                  isActive={currentStep === step}
                />
              ) : null,
            )}
          </NavigationSteps>
        </HeaderContent>
      ) : null}
    </Header>
  );
};

const HeaderArea = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const HeaderCloseButton = styled.button<HeaderProps>`
  display: inline-block;
  border: none;
  width: 32px;
  height: 32px;
  cursor: pointer;
  background: none;

  > svg path {
    stroke: ${(props) => props.textColor};
  }
`;

const ProductOriginLabel = styled.p`
  ${BodyRegularMedium}
  && {
    color: inherit;
  }
`;

const Header = styled.header<HeaderProps>`
  position: relative;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.mainColor};
  padding: 24px;
  ${device.smallTablet} {
    padding: 28px 24px;
  }
`;

const Title = styled.h3`
  flex: auto;
  ${Heading6};
  && {
    color: inherit;
  }
  ${device.smallTablet} {
    ${Heading5}
  }
`;
const ProductInformation = styled.div``;

const NavigationSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${device.smallTablet} {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;
export default HeaderStepsNavigation;
